/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

import { MouseEvent, ReactNode } from 'react'

// @mui material components
import { Theme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'

import MDBox from 'components/MDBox'

// Declaring prop types for DataTableBodyCell
interface Props {
	children: ReactNode
	noBorder?: boolean
	align?: 'left' | 'right' | 'center'
	route?: string
	onClick?: () => void
}

function DataTableBodyCell({ noBorder, align, children, route, onClick }: Props): JSX.Element {
	const navigate = useNavigate()
	const isClickable = Boolean(route || onClick)

	const handleLinkClick = (event: MouseEvent<HTMLElement>) => {
		if (!isClickable) return

		// Only intercept an unmodified primary-button click.
		// Let the browser handle right-click, middle-click, and modifier-click (open in new tab, etc.).
		const isPrimaryButton = event.button === 0
		const hasModifier = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
		if (!isPrimaryButton || hasModifier) return

		event.preventDefault()
		if (onClick) {
			onClick()
			return
		}
		if (route) {
			navigate(route)
		}
	}

	return (
		<MDBox
			component="td"
			textAlign={align}
			py={1.5}
			px={3}
			style={{ cursor: isClickable ? 'pointer' : undefined }}
			sx={({ palette: { light }, typography: { size }, borders: { borderWidth } }: Theme) => ({
				fontSize: size.sm,
				borderBottom: noBorder ? 'none' : `${borderWidth[1]} solid ${light.main}`,
			})}
		>
			<MDBox
				component={route && !onClick ? 'a' : 'span'}
				href={route && !onClick ? route : undefined}
				onClick={isClickable ? handleLinkClick : undefined}
				display="block"
				width="100%"
				color="text"
				sx={{
					verticalAlign: 'middle',
					textDecoration: 'none',
					color: 'inherit',
				}}
			>
				{children}
			</MDBox>
		</MDBox>
	)
}

// Declaring default props for DataTableBodyCell
DataTableBodyCell.defaultProps = {
	noBorder: false,
	align: 'left',
}

export default DataTableBodyCell
